import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import {
  Card,
  Badge,
  Button,
  Select,
  getStatusBadgeVariant,
  Pagination,
  CompactFilterBar,
  Table,
} from '@/components/common';
import type { Column } from '@/components/common/Table';
import { EntityLink } from '@/components/common';
import { useUnifiedChannels } from '@/hooks/useChannels';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useFilterParams } from '@/hooks/useFilterParams';
import { channelIdOf, type UnifiedChannelRow } from '@/services/channels';
import { titleCase } from '@/utils';
import { AddChannelModal } from './AddChannelModal';
import { REGISTER_AS_POSP_TAB, RegisterAsPospPanel } from './PospMappingRequestsPage';

const ITEMS_PER_PAGE = 20;

type DisplayRow = { row: UnifiedChannelRow; depth: number };

/**
 * Order rows so advertiser-linked POSPs render nested (depth 1) directly under
 * their advertiser (depth 0). POSPs whose advertiser isn't on the current page,
 * standalone POSPs, and other channels render at the top level. Grouping is
 * within the current page (the endpoint paginates a merged list).
 */
function buildDisplayRows(rows: UnifiedChannelRow[]): DisplayRow[] {
  const advertiserIds = new Set(
    rows.filter((r) => r.type_label === 'ADVERTISER').map((r) => r.id),
  );
  const childrenByParent = new Map<string, UnifiedChannelRow[]>();
  for (const r of rows) {
    if (r.parent_channel_id && advertiserIds.has(r.parent_channel_id)) {
      const list = childrenByParent.get(r.parent_channel_id) ?? [];
      list.push(r);
      childrenByParent.set(r.parent_channel_id, list);
    }
  }
  const out: DisplayRow[] = [];
  for (const r of rows) {
    // Children are emitted under their advertiser, not at the top level.
    if (r.parent_channel_id && advertiserIds.has(r.parent_channel_id)) continue;
    out.push({ row: r, depth: 0 });
    if (r.type_label === 'ADVERTISER') {
      for (const child of childrenByParent.get(r.id) ?? []) {
        out.push({ row: child, depth: 1 });
      }
    }
  }
  return out;
}

// Status filter → maps to Channel.is_active (Active/Inactive) or omitted for All.
const STATUS_OPTIONS = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

// Channel type filter. Values must match Channel.TYPE_CHOICES (backend) exactly
// and are matched against `type_label`, never `type_display`. Board #252: a
// custom channel type IS its base type, so filtering "Consolidator" correctly
// returns the broker's Bancassurance channels too -- that is the design, not a
// leak. Adding custom names here would filter on a different axis.
//
// Review follow-up: that comment described a filter that did not exist --
// CONSOLIDATOR was missing from this list, even though it is offered in the
// Add Channel picker AND is the base every #252 example ("Bancassurance") is
// built on. A Consolidator-based custom type was therefore unfilterable on the
// page whose filter is meant to reach it. Every Channel.TYPE_CHOICES member is
// listed here now.
const CHANNEL_TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'POSP', label: 'POSP' },
  { value: 'AGENT', label: 'Agent' },
  { value: 'ADVERTISER', label: 'Advertiser' },
  { value: 'CONSOLIDATOR', label: 'Consolidator' },
  { value: 'BRANCH', label: 'Branch' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'OTHER', label: 'Other' },
];

const CHANNEL_TYPE_VALUES = new Set(CHANNEL_TYPE_OPTIONS.map((o) => o.value).filter(Boolean));

/**
 * Q-01 -- the filter state this page mirrors into the query string.
 *
 * Module-level: `useFilterParams` reads it once for the keys it owns and each
 * key's shape. DELIBERATELY NOT HERE: the page number (every filter change
 * below resets it to 1, so a restored number would be clobbered before the
 * first paint) and the sort (see SORTABLE_FIELDS -- this endpoint has none).
 */
type ChannelPanelFilters = {
  search: string;
  status: string;
  type: string;
};

const EMPTY_FILTERS: ChannelPanelFilters = {
  search: '',
  status: '',
  type: '',
};

/** The house namespace. REQUIRED here: this page also honours an unprefixed
 *  `?type=` deep link (below), and writing the user's own choice back into that
 *  same param is exactly how a one-shot scope turns permanent. */
const FILTER_PREFIX = 'f_';

/**
 * Every UNPREFIXED param this page treats as a ONE-SHOT deep-link seed.
 *
 * `?type=POSP` is real and load-bearing: `/posps` and `/posps/:id` both route to
 * `LegacyPospRedirect`, which forwards a broker role to
 * `/broker/channels?type=POSP` (routing/legacyPospRedirect.ts). Until now this
 * page ignored that param outright -- every agent link in the app landed on the
 * UNFILTERED channels list, which is the whole point of the redirect missed.
 *
 * `useFilterParams` owns only the `f_` namespace, so neither `clearAll()` nor a
 * filter the user empties can remove an unprefixed param. Left in place it
 * outranks the user on the NEXT mount, because `initial` re-seeds from it and
 * an empty `f_` value is DELETED rather than written -- the namespace has no
 * way to say "deliberately empty". That is two shipped bugs on the pilot page:
 * Clear Filters undone by a refresh, and a deep-linked filter the user cleared
 * by hand coming back on reload. So the seed is CONSUMED: once the user has
 * overridden the key it seeded (or cleared everything), it is deleted from the
 * URL, having done its one job.
 */
const DEEP_LINK_PARAMS = ['type'] as const;

/** Which unprefixed seed param(s) a given panel filter overrides. */
const SEED_PARAMS_FOR: Partial<Record<keyof ChannelPanelFilters, readonly string[]>> = {
  type: ['type'],
};

/**
 * U-04: NO column on this page can be sorted, and that is the honest answer
 * rather than a missing feature.
 *
 * The rows come from `BrokerChannelViewSet.unified`
 * (backend/apps/brokers/views.py:5803), which merges POSP rows and Channel rows
 * in Python and paginates the merged list by hand. It reads exactly one query
 * param -- `search` -- plus `id`/`type` lookups; the viewset's `filter_backends`
 * are `[DjangoFilterBackend, SearchFilter]` with NO `OrderingFilter` and no
 * `ordering_fields` (views.py:5171-5174), so `?ordering=` is not dropped by DRF
 * here -- it is never looked at by anything.
 *
 * A sortable header would therefore be a control that does nothing at all,
 * which this project has shipped before. So the set of sortable fields here is
 * EMPTY, no column carries `sortable`, and `onSortChange` is not passed to the
 * `Table` below either -- without that handler no header renders as a button at
 * all. Sorting this list needs the endpoint to accept an order first.
 */

export function ChannelsListPage() {
  const [page, setPage] = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<UnifiedChannelRow | undefined>();
  const [searchParams, setSearchParams] = useSearchParams();
  const seededType = searchParams.get('type') || '';
  // M-02: "Register as POSP" (agents asking to join this brokerage) is a TAB
  // of Channels, not a page reached only through a header button. Kept in the
  // URL (`?tab=register-posp`) so the old /brokers/mapping-requests link can
  // forward straight to it and a refresh stays put.
  const onRegisterTab = searchParams.get('tab') === REGISTER_AS_POSP_TAB;
  const selectTab = (registerTab: boolean) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (registerTab) next.set('tab', REGISTER_AS_POSP_TAB);
        else next.delete('tab');
        return next;
      },
      { replace: true }
    );
  };

  // Q-01: the panel's own filters, mirrored into `?f_*` so they survive a
  // refresh, a bookmark and a shared link. The unprefixed `?type=` deep link is
  // read ONCE here, in the hook's LAZY INITIALIZER (never in an effect: the
  // filter panel decides on its FIRST render whether to open, and a seed that
  // arrived on render two would leave a filtered list behind a collapsed
  // panel), and merged UNDER anything already in the `f_` namespace -- so a
  // value the user actually chose always beats the seed.
  const { filters, setFilters, clearAll } = useFilterParams<ChannelPanelFilters>({
    defaults: EMPTY_FILTERS,
    prefix: FILTER_PREFIX,
    initial: (params) => {
      // An unknown type is dropped rather than applied: it would filter every
      // row away client-side and read as "this broker has no channels".
      const rawType = params.get('type') || '';
      return { type: CHANNEL_TYPE_VALUES.has(rawType) ? rawType : '' };
    },
  });

  // Global rule 1 -- search-as-you-type without a request per keystroke.
  // Seeded from the restored `?f_search=` so a refresh brings the box back
  // filled in, not just the results.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch(filters.search);

  // Only a type the picker actually offers is sent. A hand-edited or stale
  // `?f_type=` would otherwise be matched against `type_label` server-side,
  // filter every row away, and read as "this broker has no channels".
  const typeFilter = CHANNEL_TYPE_VALUES.has(filters.type) ? filters.type : '';

  const { data, isLoading, isError, refetch } = useUnifiedChannels(
    page,
    filters.search || undefined,
    typeFilter || undefined
  );
  // SEARCH and TYPE are server-side (both are applied before `unified`
  // paginates, so `count` and the pager agree with what is on screen). STATUS
  // is not: the endpoint has no is_active filter, so it stays a client-side
  // narrowing of the loaded page -- which means the header count is the
  // unfiltered one while it is set. That is a smaller lie than the type filter
  // was (it dropped agents on page 2 out of a list that claimed to be "POSP"),
  // and closing it needs the endpoint to take a status param.
  const rows = useMemo(
    () =>
      (data?.results ?? []).filter((row) => {
        if (filters.status === 'active' && row.status !== 'active') return false;
        if (filters.status === 'inactive' && row.status === 'active') return false;
        return true;
      }),
    [data, filters.status]
  );
  // Nest advertiser-linked POSPs under their advertiser so the relationship —
  // and both commissions — are visible together.
  const displayRows = useMemo(() => buildDisplayRows(rows), [rows]);
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);

  // Push the debounced term into the filters and reset to page 1. Keyed off a
  // ref of the LAST TERM PUSHED (not `filters.search`), so the mount pass is a
  // no-op for a restored search and Clear is not immediately undone by the
  // 300ms of stale `debouncedTerm` still in flight. Routing the query through
  // `filters.search` rather than `debouncedTerm` also keeps the page reset and
  // the new term in ONE batched render -- reading `debouncedTerm` directly used
  // to fire a fetch with the OLD page number against the NEW term, which DRF
  // 404s when that page no longer exists for the narrowed result set.
  const pushedTermRef = useRef(filters.search);
  useEffect(() => {
    if (pushedTermRef.current === debouncedTerm) return;
    pushedTermRef.current = debouncedTerm;
    setFilters({ search: debouncedTerm });
    setPage(1);
  }, [debouncedTerm, setFilters]);

  /**
   * Delete the named UNPREFIXED deep-link params from the URL -- see
   * `DEEP_LINK_PARAMS`. Called whenever the user overrides a key one of them
   * seeded, so the seed cannot come back and outrank them on the next mount.
   *
   * Two writers in one tick is safe, and deliberately so: `useFilterParams`
   * defers ITS write to a layout effect and issues it with the functional form
   * against the query string react-router has actually committed, precisely for
   * this co-tenant case.
   *
   * No-op when none of them is present, so an ordinary filter change on an
   * ordinary /broker/channels URL costs no extra navigation.
   */
  const consumeSeedParams = (names: readonly string[]) => {
    if (!names.some((name) => searchParams.has(name))) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        for (const name of names) next.delete(name);
        return next;
      },
      { replace: true }
    );
  };

  const handleFilterChange = (key: keyof ChannelPanelFilters, value: string) => {
    setFilters({ [key]: value } as Partial<ChannelPanelFilters>);
    consumeSeedParams(SEED_PARAMS_FOR[key] ?? []);
    setPage(1);
  };

  // Task 10: the count badges the PANEL's own controls only -- not the search
  // box (always visible, so it explains itself) and not the deep-link scope.
  const activeFilterCount = [filters.status, filters.type].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm('');
    clearAll();
    // ...and THIS is what makes Clear survive a refresh. `clearAll` empties the
    // `f_` namespace only; a surviving `?type=POSP` would re-seed the filter
    // (and the banner) the moment the page was reloaded or the link bookmarked.
    consumeSeedParams(DEEP_LINK_PARAMS);
    setPage(1);
  };

  // "Arrived from a deep link AND it is still what is applied" -- keyed off the
  // unprefixed param AND the applied value, so a user who arrives via
  // ?type=POSP and then picks Advertiser (or clears it) stops being told the
  // list is scoped to agents.
  const isScoped = Boolean(seededType && typeFilter === seededType);

  /**
   * U-02: the columns, with a width each.
   *
   * SIZE = CONTENT + CELL PADDING. `Table` pads every `<td>` with `px-6` (48px)
   * and sets `truncate` under `layout="fixed"` -- overflow:hidden, no scrollbar
   * and no wrap -- so a column declared 9rem offers 96px of content and
   * anything wider is CLIPPED. The numeric and badge columns override the
   * padding to `px-3` (24px) through `className` (`cn` is tailwind-merge, so a
   * `px-*` there beats the `px-6`):
   *
   *   name       20rem px-6 -> 272px (nesting indent 24px + name + email line)
   *   type        9rem px-3 -> 120px (a sm badge of a custom type name)
   *   code        9rem px-6 ->  96px (font-mono)
   *   policies    6rem px-3 ->  72px
   *   commission  8rem px-3 -> 104px
   *   earned     12rem px-3 -> 144px (a lifetime rupee figure)
   *   status      8rem px-3 -> 104px ("Inactive" as a sm badge: ~70px)
   *   actions     6rem px-3 ->  72px (the "Edit" text button: ~30px)
   *             -------
   *              78rem = 1248px, inside the ~1424px a 1440px desktop can show
   *   with no horizontal page scrollbar. Narrower than that the table -- not
   *   the page -- scrolls sideways inside its own card.
   *
   * Actions holds an INTERACTIVE control, so it is sized off the real classes
   * rather than by eye: under `layout="fixed"` a too-narrow cell does not
   * scroll and does not ellipsise, the button simply ceases to exist.
   *
   * Every column whose render() returns JSX carries its OWN `title`: `Table`
   * sets a tooltip only for string/number content, so money, badges and links
   * would otherwise be cut off with nothing to hover. Each badge gets
   * `max-w-full` with its label in a `truncate` span -- an `inline-flex` badge
   * is an atomic inline box that a `truncate` ancestor hard-CLIPS.
   */
  const columns: Column<DisplayRow>[] = [
    {
      key: 'name',
      header: 'Name',
      width: '20rem',
      render: ({ row, depth }: DisplayRow) => (
        <div style={{ paddingLeft: depth * 24 }} className="flex items-start gap-2">
          {depth > 0 && (
            // Was text-gray-300 on white (~1.6:1). It is the only mark of the
            // parent/child relationship in the row, so it has to be readable.
            <span className="text-gray-500 dark:text-gray-400 select-none" aria-hidden="true">
              └
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="truncate" title={row.name}>
                <EntityLink kind={row.entity_kind} id={row.id} name={row.name} />
              </span>
              {/* B7: a POSP-type channel saved WITHOUT a login (its
                  email/phone matched an existing account) has no
                  agent to sign in as -- flagged here so the broker
                  knows to give it a different login email rather
                  than wondering why the agent can't sign in. */}
              {row.type_label === 'POSP' && row.has_login === false && (
                <Badge variant="default" size="sm" className="shrink-0">
                  No login
                </Badge>
              )}
            </div>
            {depth > 0 && row.parent_channel_name && (
              // Was text-gray-400 on white (~2.8:1, under the 4.5:1 floor).
              <p
                className="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5"
                title={`under ${row.parent_channel_name}`}
              >
                under {row.parent_channel_name}
              </p>
            )}
            {row.email && (
              <p className="truncate text-xs text-gray-500 dark:text-gray-400 mt-0.5" title={row.email}>
                {row.email}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '9rem',
      className: 'px-3',
      render: ({ row }: DisplayRow) => {
        const label = row.type_display ?? row.type_label;
        const custom = row.type_display && row.type_display !== row.type_label;
        return (
          // Colour still keys off type_label (behaviour); the text is
          // type_display so a custom type shows its own name. The wrapper
          // carries the tooltip -- Badge takes no title prop and is shared, so
          // it is not changed.
          <span
            className="block max-w-full"
            title={custom ? `Custom channel type — behaves as ${row.type_label}` : label}
          >
            <Badge
              variant={row.type_label === 'ADVERTISER' ? 'warning' : 'info'}
              size="sm"
              className="max-w-full"
            >
              <span className="truncate">{label}</span>
            </Badge>
          </span>
        );
      },
    },
    {
      key: 'code',
      header: 'Code',
      width: '9rem',
      className: 'text-gray-700 dark:text-gray-300 font-mono',
      render: ({ row }: DisplayRow) => row.code || '-',
    },
    {
      key: 'policies',
      header: 'Policies',
      width: '6rem',
      className: 'px-3 text-right text-gray-900 dark:text-gray-100',
      render: ({ row }: DisplayRow) => row.policy_count,
    },
    {
      key: 'commission_percentage',
      header: 'Commission %',
      width: '8rem',
      className: 'px-3 text-right text-gray-900 dark:text-gray-100',
      render: ({ row }: DisplayRow) =>
        row.commission_percentage != null ? `${row.commission_percentage}%` : '-',
    },
    {
      key: 'commission_earned',
      header: 'Commission Earned (Lifetime)',
      width: '12rem',
      className: 'px-3 text-right',
      render: ({ row }: DisplayRow) =>
        row.commission_earned === 'N-A' ? (
          // "not applicable", not "nothing earned" -- a channel type that
          // cannot earn commission at all. gray-400 on white was ~2.8:1.
          <span
            className="block truncate text-gray-500 dark:text-gray-400"
            title="Not applicable to this channel type"
          >
            N/A
          </span>
        ) : (
          <span
            className="block truncate text-gray-900 dark:text-gray-100"
            title={row.commission_earned}
          >
            {row.commission_earned}
          </span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      className: 'px-3',
      render: ({ row }: DisplayRow) => (
        <Badge variant={getStatusBadgeVariant(row.status)} size="sm" className="max-w-full">
          <span className="truncate" title={titleCase(row.status)}>
            {titleCase(row.status)}
          </span>
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '6rem',
      className: 'px-3 text-right',
      render: ({ row }: DisplayRow) =>
        // A POSP with no channel row has nothing to PATCH.
        channelIdOf(row) ? (
          <button
            type="button"
            onClick={() => setEditing(row)}
            className="text-sm font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            aria-label={`Edit ${row.name}`}
          >
            Edit
          </button>
        ) : (
          <span className="text-xs text-gray-500 dark:text-gray-400" title="This channel has no editable record">
            —
          </span>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Channels"
        subtitle={`${totalItems} channel${totalItems !== 1 ? 's' : ''} & POSPs`}
        breadcrumbs={[
          { label: 'Broker', href: '/dashboard' },
          { label: 'Channels' },
        ]}
        actions={
          <div className="flex gap-2">
            {/* Board #252: /broker/channel-types was routed in App.tsx but linked
                from nowhere -- reachable only by typing the URL. This is the
                entry point to creating a custom channel type. */}
            <Link to="/broker/channel-types">
              <Button variant="outline" leftIcon={<Squares2X2Icon className="w-5 h-5" />}>
                Manage Channel Types
              </Button>
            </Link>
            <Button leftIcon={<PlusIcon className="w-5 h-5" />} onClick={() => setShowAdd(true)}>
              Add Channel
            </Button>
          </div>
        }
      />

      <AddChannelModal isOpen={showAdd} onClose={() => setShowAdd(false)} />
      {/* Separate mount: one modal serving both modes would have the edit prefill
          effect fight the create flow. */}
      <AddChannelModal
        isOpen={Boolean(editing)}
        channel={editing}
        onClose={() => setEditing(undefined)}
      />

      <div className="mb-4 border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
        <nav className="flex space-x-6" role="tablist" aria-label="Channels sections">
          {[
            { register: false, label: 'Channels & POSPs' },
            { register: true, label: 'Register as POSP' },
          ].map((tab) => (
            <button
              key={tab.label}
              type="button"
              role="tab"
              aria-selected={onRegisterTab === tab.register}
              onClick={() => selectTab(tab.register)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                onRegisterTab === tab.register
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:hover:border-gray-600 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {onRegisterTab ? (
        <div role="tabpanel" aria-label="Register as POSP">
          <RegisterAsPospPanel showToolbar />
        </div>
      ) : (
          <div role="tabpanel" aria-label="Channels & POSPs">
          {isScoped && (
            <div className="mb-4 flex items-center justify-between rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm dark:border-primary-800 dark:bg-primary-900/20">
              <span className="text-primary-800 dark:text-primary-200">
                Showing the{' '}
                {CHANNEL_TYPE_OPTIONS.find((o) => o.value === seededType)?.label ?? seededType} channels
                only.
              </span>
              <button
                type="button"
                onClick={clearFilters}
                className="font-medium text-primary-700 underline dark:text-primary-300"
              >
                Show all channels
              </button>
            </div>
          )}

          <CompactFilterBar
            search={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Search by name, code, or email..."
            activeFilterCount={activeFilterCount}
            onClearFilters={clearFilters}
          >
            <Select
              id="channel-filter-status"
              label="Status"
              options={STATUS_OPTIONS}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            />
            <Select
              id="channel-filter-type"
              label="Channel Type"
              options={CHANNEL_TYPE_OPTIONS}
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
            />
          </CompactFilterBar>

          {/* Three states, never two: a failed request, an empty book and a loading
              list are different facts. This page rendered the empty state for all
              of them -- a broker whose channels request 500d was told they had no
              channels, and offered an Add Channel button as the remedy. */}
          {isLoading ? (
            <Table<DisplayRow> data={[]} columns={columns} isLoading rowKey={keyOf} />
          ) : isError ? (
            <Card padding="none">
              <div role="alert" className="px-6 py-12 text-center">
                <p className="text-sm font-medium text-red-700 dark:text-red-400">
                  Could not load channels.
                </p>
                <button
                  type="button"
                  className="mt-3 text-sm text-primary-600 underline hover:no-underline dark:text-primary-400"
                  onClick={() => void refetch()}
                >
                  Try again
                </button>
              </div>
            </Card>
          ) : rows.length === 0 ? (
            <Card padding="none">
              <EmptyState onAdd={() => setShowAdd(true)} />
            </Card>
          ) : (
            <>
              <Table<DisplayRow>
                data={displayRows}
                columns={columns}
                rowKey={keyOf}
                // U-02: declared widths (no column jump when the filters change)
                // and a header that stays put while the rows scroll under it.
                layout="fixed"
                stickyHeader
                // U-04: no `onSortChange`. See SORTABLE_FIELDS -- this endpoint
                // cannot order anything, so no header may pretend to.
              />
              {totalPages > 1 && (
                <Card padding="none" className="mt-4 overflow-hidden">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setPage}
                  />
                </Card>
              )}
            </>
          )}
          </div>
      )}
    </div>
  );
}

/** A POSP row and its channel row can share an id, so the kind is part of the key. */
function keyOf({ row }: DisplayRow): string {
  return `${row.entity_kind}-${row.id}`;
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-white">No channels found</h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        No channels or POSPs match your search criteria.
      </p>
      <Button className="mt-4" leftIcon={<PlusIcon className="w-5 h-5" />} onClick={onAdd}>
        Add Channel
      </Button>
    </div>
  );
}
